import React, { useMemo, useState } from 'react';
import { type ComponentSize } from 'src/modules/shared/types/component.type';
import MenuDropdown from './dropdowns/menu-dropdown';
import type { DropdownItem } from './dropdowns/dropdown';
import { PiUserCircle, PiUserLight, PiXLight } from 'react-icons/pi';
import { AiRobotOutlineIcon } from 'src/modules/shared/components/atoms/indicators/ai-badge';
import { Text } from '../atoms/text';
import Tooltip from '../atoms/tooltips/tooltip';
import {
  getMenuItemContentClasses,
  getMenuItemContentColor,
  getMenuItemContentWeight,
} from 'src/modules/shared/styles/dropdown.styles';
import { ButtonSkeleton } from '../atoms/buttons/button-skeleton';
import { classes } from 'src/modules/shared/utils/styles.util';
import { getControlTextSize } from 'src/modules/shared/components/atoms/text/text.styles';
import { UserKind } from 'src/api/generated';

export interface UserOption {
  id: string | number;
  label: string;
  kind?: UserKind;
}

interface UserSelectorProps {
  users: UserOption[];
  /** User IDs represented by the active filter selection. */
  selectedUserIds: readonly (string | number)[];
  /** Builds the localized trigger label for multiple selected users. */
  multipleSelectionLabel: (count: number) => string;
  onUserSelect: (id: string | number) => void;
  size?: ComponentSize;
  className?: string;
  handlerContainerClassName?: string;
  showAll?: boolean;
  allLabel?: string;
  loading?: boolean;
  tooltipContent?: string | ((selectedLabel: string) => string);
}

/**
 * UserSelector component that displays available users in a dropdown.
 * Allows filtering by selecting a specific user or viewing all.
 */
export const UserSelector: React.FC<UserSelectorProps> = ({
  users,
  selectedUserIds,
  multipleSelectionLabel,
  onUserSelect,
  size = 'md',
  className = '',
  handlerContainerClassName = '',
  // TODO: check when implementing i18n
  allLabel = undefined,
  loading = false,
  tooltipContent = '',
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const normalizedSelectedUserIds = useMemo(
    () => Array.from(new Set(selectedUserIds.map(String))),
    [selectedUserIds]
  );
  const hasNoSelection = normalizedSelectedUserIds.length === 0;
  const hasSingleSelection = normalizedSelectedUserIds.length === 1;
  const selectedUserId = normalizedSelectedUserIds[0];

  /**
   * Build dropdown items for user selector
   */
  const dropdownItems: DropdownItem[] = useMemo(() => {
    const items: DropdownItem[] = [];

    // Add "All" option if enabled
    if (allLabel) {
      items.push({
        content: (
          <Text
            size={getControlTextSize('lg')}
            weight={getMenuItemContentWeight(hasNoSelection)}
            color={getMenuItemContentColor(hasNoSelection)}
            className={getMenuItemContentClasses()}
          >
            {allLabel}
          </Text>
        ),
        onClick: () => onUserSelect(''),
        selected: hasNoSelection,
      });

      if (users.length > 0) {
        items.push({
          separator: true,
          content: null,
          onClick: () => undefined,
        });
      }
    }

    const personUsers = users.filter((user) => user.kind !== UserKind.SYSTEM);
    const systemUsers = users.filter((user) => user.kind === UserKind.SYSTEM);
    const hasSystemUsers = systemUsers.length > 0;

    const buildUserItem = (user: UserOption): DropdownItem => {
      const isSelected =
        hasSingleSelection && selectedUserId === String(user.id);
      return {
        content: (
          <Text
            size={getControlTextSize('lg')}
            weight={getMenuItemContentWeight(isSelected)}
            color={getMenuItemContentColor(isSelected)}
            className={getMenuItemContentClasses()}
          >
            {user.label}
          </Text>
        ),
        icon: hasSystemUsers ? (
          user.kind === UserKind.SYSTEM ? (
            <AiRobotOutlineIcon className="h-4 w-4 text-secondary-600" />
          ) : user.kind === UserKind.PERSON ? (
            <PiUserLight className="h-4 w-4 text-secondary-600" />
          ) : undefined
        ) : undefined,
        onClick: () => onUserSelect(user.id),
        selected: isSelected,
      };
    };

    const unassignedUser = personUsers.find(
      (user) => String(user.id) === '0' || user.label === 'Sin asignar'
    );
    const filteredPersonUsers = personUsers.filter(
      (user) => user !== unassignedUser
    );

    if (unassignedUser) {
      const isSelected =
        hasSingleSelection && selectedUserId === String(unassignedUser.id);
      items.push({
        content: (
          <Text
            size={getControlTextSize('lg')}
            weight={getMenuItemContentWeight(isSelected)}
            color={getMenuItemContentColor(isSelected)}
            className={getMenuItemContentClasses()}
          >
            {unassignedUser.label}
          </Text>
        ),
        icon: (
          <PiXLight className="h-4 w-4 text-secondary-600 absolute right-4" />
        ),
        onClick: () => onUserSelect(unassignedUser.id),
        selected: isSelected,
        separator: filteredPersonUsers.length > 0 || hasSystemUsers,
      });
    }

    items.push(...filteredPersonUsers.map(buildUserItem));

    if (filteredPersonUsers.length > 0 && hasSystemUsers) {
      items.push({
        separator: true,
        content: null,
        onClick: () => undefined,
      });
    }

    if (hasSystemUsers) {
      items.push(...systemUsers.map(buildUserItem));
    }

    return items;
  }, [
    allLabel,
    hasNoSelection,
    hasSingleSelection,
    onUserSelect,
    selectedUserId,
    users,
  ]);

  const selectedUser = useMemo(
    () => users.find((user) => String(user.id) === String(selectedUserId)),
    [users, selectedUserId]
  );
  const selectedLabel = hasNoSelection
    ? allLabel
    : normalizedSelectedUserIds.length > 1
      ? multipleSelectionLabel(normalizedSelectedUserIds.length)
      : // TODO: check when implementing i18n
        selectedUser?.label ?? 'Selecciona un usuario';

  const tooltipContentText = useMemo(() => {
    if (!selectedLabel) return '';

    return typeof tooltipContent === 'function'
      ? tooltipContent(selectedLabel)
      : tooltipContent;
  }, [selectedLabel, tooltipContent]);

  if (loading) {
    return (
      <ButtonSkeleton size={size} withIcon variant="secondary" fullWidth />
    );
  }

  const dropdownClasses = classes('w-full justify-between', className);
  const dropdownContainerClasses = classes(
    'w-full whitespace-nowrap text-ellipsis overflow-hidden',
    handlerContainerClassName
  );

  return (
    <Tooltip
      enabled={!isDropdownOpen}
      content={tooltipContentText}
      placement="bottom"
      size="md"
      className="w-[16rem]"
    >
      <MenuDropdown
        items={dropdownItems}
        icon={<PiUserCircle className="w-4 h-4" />}
        label={
          <Text
            size={getControlTextSize(size)}
            weight={getMenuItemContentWeight(false)}
            color={getMenuItemContentColor(false)}
            className={`${getMenuItemContentClasses()} whitespace-nowrap truncate`}
          >
            <span className="hidden xl:inline">
              {/* TODO: check when implementing i18n */}
              Asignado:{' '}
            </span>
            {selectedLabel}
          </Text>
        }
        variant="secondary"
        appearance="outline"
        size={size}
        className={dropdownClasses}
        handlerContainerClassName={dropdownContainerClasses}
        placement="bottom-start"
        onOpenChange={setIsDropdownOpen}
      />
    </Tooltip>
  );
};
